// Collaboration provider — the cross-cutting realtime-ish state that isn't tied to one screen:
//   • polls for incoming nudges + my unread message count,
//   • runs the co-op session loop while co-op is on: my room, page, pointer, shared filters and scroll,
//     and what I'm doing (a click, the field I'm in, my last save) out; the room's people, page, filters
//     and scroll back (rules in packages/shared/src/domain/coop.ts), and a save by anyone in the room
//     refreshes the others' data,
//   • owns the chat panel open/active-thread state and a toast queue for nudges.
// All transport is polling over the existing /api — no sockets — so it works on the Pages deploy.
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { APP_VERSION, notificationLink, type CoopActivity, type CoopAnchor, type CoopClick, type CoopScroll } from '@technohull/shared';
import { api } from '../lib/api.js';
import { useNotifications } from '../features/hooks.js';
import { useCreateThread, useNudge, type CollabNudge, type CollabThread, type CoopPeer } from './collabHooks.js';
import { messageBubble, notificationBubbles, safeToRefresh } from './toastRules.js';
import { anchorOf, isPageAnchor } from './coopAnchor.js';
import { createScrollShare, frameOf, isEditableField, pageScroller, type ScrollShare } from './coopScroll.js';

/** someone else in my co-op room: where they are and point (CoopPeer), and what they're doing. */
export type CoopMate = CoopPeer & CoopActivity;

/** a generic pop-up toast (notifications, messages, a new-version notice) that shares the nudge toast UI.
 *  A sticky one stays until it's dismissed or acted on (the new-version notice, until the page refreshes). */
export interface AppToastData { icon: 'bell' | 'chat' | 'update'; title: string; body?: string; actionLabel?: string; action?: () => void; sticky?: boolean }
export type Toast = { id: number; nudge: CollabNudge } | { id: number; app: AppToastData };

interface CollabValue {
  unread: number;
  // chat panel
  chatOpen: boolean;
  activeThread: string | null;
  openChat: (threadId?: string) => void;
  closeChat: () => void;
  setActiveThread: (id: string | null) => void;
  openDmWith: (uid: string) => void;
  /** why the last openDmWith couldn't start the conversation (the chat list shows it); null when fine. */
  dmError: Error | null;
  clearDmError: () => void;
  // nudges
  nudge: (uid: string, kind?: 'poke' | 'coop') => void;
  /** the last nudge / co-op invite that failed to send (CollabDock shows it); null when fine. */
  nudgeError: { kind: 'poke' | 'coop'; error: Error } | null;
  clearNudgeError: () => void;
  toasts: Toast[];
  dismissToast: (id: number) => void;
  /** answer a co-op invite: go to its page and join the inviter's room (no room: whoever's on that page). */
  joinCoop: (path: string, room?: string | null) => void;
  // co-op sessions
  coopOn: boolean;
  coopPeers: CoopMate[];
  toggleCoop: () => void;
  /** whether my view follows the room's scroll (and my scrolling leads it); on unless switched off. */
  coopScrollTogether: boolean;
  setCoopScrollTogether: (on: boolean) => void;
  /** whether my view is waiting for a field I'm editing, so it isn't where the room's is right now. */
  coopViewHeld: () => boolean;
  /** whether it waits while the room's scroll has moved on (the co-op banner says so). */
  coopViewPaused: () => boolean;
  startCoopWith: (uid: string) => void;
  // a page (or a component within it) registers a keyed slice of shareable filter/view state (JSON) +
  // how to apply an incoming one, so co-op keeps everyone's filters in sync too (see useCoopView).
  registerCoopView: (key: string, view: string | null, apply: ((v: Record<string, unknown>) => void) | null) => void;
}

const Ctx = createContext<CollabValue | null>(null);

// a short two-tone chime for an incoming nudge (WebAudio, no asset needed).
function playChime() {
  try {
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ac = new AC();
    const now = ac.currentTime;
    [880, 1180].forEach((f, i) => {
      const o = ac.createOscillator();
      const g = ac.createGain();
      o.frequency.value = f;
      o.type = 'sine';
      g.gain.setValueAtTime(0.0001, now + i * 0.12);
      g.gain.exponentialRampToValueAtTime(0.15, now + i * 0.12 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.12 + 0.18);
      o.connect(g);
      g.connect(ac.destination);
      o.start(now + i * 0.12);
      o.stop(now + i * 0.12 + 0.2);
    });
    setTimeout(() => ac.close().catch(() => {}), 600);
  } catch {
    /* audio blocked — the visual toast still shows */
  }
}

// co-op survives a reload (and the refresh a new version triggers): the tab remembers its room for a
// couple of minutes. Per tab on purpose, so a second tab isn't pulled into the session.
const COOP_KEY = 'rolodex.coop';
const COOP_RESUME_MS = 120_000;
interface CoopRoom { id: string | null; named: boolean }
function readCoop(): CoopRoom | null {
  try {
    const v = JSON.parse(sessionStorage.getItem(COOP_KEY) ?? 'null') as { id?: unknown; named?: unknown; at?: unknown } | null;
    if (!v || typeof v.at !== 'number' || Date.now() - v.at > COOP_RESUME_MS) return null;
    return { id: typeof v.id === 'string' ? v.id : null, named: v.named === true };
  } catch {
    return null;
  }
}
function writeCoop(room: CoopRoom | null) {
  try {
    if (room) sessionStorage.setItem(COOP_KEY, JSON.stringify({ ...room, at: Date.now() }));
    else sessionStorage.removeItem(COOP_KEY);
  } catch { /* storage blocked: co-op just won't survive a reload */ }
}
// "Scroll together" can be switched off for a while; the tab remembers it (on unless said otherwise).
const COOP_SCROLL_KEY = 'rolodex.coopScroll';
function readScrollTogether(): boolean {
  try { return sessionStorage.getItem(COOP_SCROLL_KEY) !== '0'; } catch { return true; }
}
function writeScrollTogether(on: boolean) {
  try {
    if (on) sessionStorage.removeItem(COOP_SCROLL_KEY);
    else sessionStorage.setItem(COOP_SCROLL_KEY, '0');
  } catch { /* storage blocked: it just won't last */ }
}

/** the field I'm editing on the page, as the others can find it: its table cell when it's in one (a
 *  cell's editor exists only on the screen of whoever opened it), else the field itself. */
function editingAnchor(): CoopAnchor | null {
  const el = document.activeElement;
  if (!isEditableField(el)) return null;
  const host = el.closest('td, th, [role="gridcell"]') ?? el;
  const r = host.getBoundingClientRect();
  const a = anchorOf(host, r.left + r.width / 2, r.top + r.height / 2);
  return a && isPageAnchor(a) ? a : null;
}

const newRoomId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID().replace(/-/g, '').slice(0, 16)
    : Array.from({ length: 16 }, () => Math.floor(Math.random() * 36).toString(36)).join('');

// a filter change counts as yours (and so leads the room) when it follows your own click or key within
// this long, or comes with a page you went to yourself; anything else (a page's defaults, data arriving,
// the room's filters being applied) is not a choice of yours and never overrides the room.
const LEAD_INPUT_MS = 1500;
const LEAD_ARRIVAL_MS = 3000;

export function CollabProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // kept current during render, so the page's own effects (which run before ours) already see it
  const pathRef = useRef(pathname);
  // a session this tab was in before a reload (see readCoop)
  const [resumed] = useState(readCoop);
  // the page the room took me to; cleared as soon as I go somewhere myself
  const followedPath = useRef<string | null>(null);
  // when (and how) I got to this page. A reload back into a session lands on the page's defaults, which
  // are no more a choice of mine than a page I was taken to, so they don't lead either.
  const arrived = useRef({ at: Date.now(), followed: resumed != null });
  if (pathRef.current !== pathname) {
    if (pathname !== followedPath.current) followedPath.current = null;
    arrived.current = { at: Date.now(), followed: followedPath.current === pathname };
    pathRef.current = pathname;
  }

  const nudgeMut = useNudge();
  const createThread = useCreateThread();

  const [chatOpen, setChatOpen] = useState(false);
  const [activeThread, setActiveThread] = useState<string | null>(null);
  // read by the poll's message bubble, which answers after a fetch: the panel as it is then
  const chatOpenRef = useRef(chatOpen);
  chatOpenRef.current = chatOpen;
  const activeThreadRef = useRef(activeThread);
  activeThreadRef.current = activeThread;
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [coopOn, setCoopOn] = useState(resumed != null);
  const [coopPeers, setCoopPeers] = useState<CoopMate[]>([]);
  const [scrollTogether, setScrollTogether] = useState(readScrollTogether);
  const scrollTogetherRef = useRef(scrollTogether);
  const scrollShare = useRef<ScrollShare | null>(null);
  const queryClient = useQueryClient();
  // my room: `named` when it came from an invite (sent or answered); otherwise the server places me with
  // whoever's in co-op on my page and `id` is just where it put me
  const room = useRef<CoopRoom>(resumed ?? { id: null, named: false });
  const toastId = useRef(1);

  // a failed DM belongs to that visit to the panel: however it gets closed, the next open starts clean.
  useEffect(() => {
    if (!chatOpen && createThread.isError) createThread.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatOpen]);

  const pushApp = useCallback((app: AppToastData) => setToasts((t) => [...t, { id: toastId.current++, app }]), []);

  // poll: incoming nudges (server drains them, so each arrives once) + unread total + the served version.
  const poll = useQuery({
    queryKey: ['collab', 'poll'],
    queryFn: () => api.post<{ nudges: CollabNudge[]; unread: number; version?: string }>('/api/collab/poll', { path: pathRef.current }),
    refetchInterval: 12_000, // nudge + unread-badge poll; 12s is plenty and cuts a per-user D1 write+read cadence
    refetchIntervalInBackground: false,
  });

  const lastProcessed = useRef(0);
  const prevUnread = useRef<number | undefined>(undefined);
  const reloading = useRef(false);
  useEffect(() => {
    if (!poll.data || poll.dataUpdatedAt === lastProcessed.current) return;
    lastProcessed.current = poll.dataUpdatedAt;
    // incoming nudges
    if (poll.data.nudges.length) {
      const fresh = poll.data.nudges;
      // a newer invite from the same person replaces the one still on screen
      const replaced = (t: Toast) => 'nudge' in t && t.nudge.kind === 'coop' && fresh.some((n) => n.kind === 'coop' && n.fromUid === t.nudge.fromUid);
      setToasts((t) => [...t.filter((x) => !replaced(x)), ...fresh.map((n) => ({ id: toastId.current++, nudge: n }))]);
      playChime();
    }
    // new chat messages: the unread total went up since the last poll → pop a toast (like a nudge) that
    // says who wrote and how their message starts, and opens that conversation
    const unread = poll.data.unread;
    if (prevUnread.current !== undefined && unread > prevUnread.current) {
      void queryClient
        .fetchQuery({ queryKey: ['collab', 'threads'], queryFn: () => api.get<{ threads: CollabThread[] }>('/api/collab/threads'), staleTime: 0 })
        .then((r) => r.threads, () => undefined)
        .then((threads) => {
          const b = messageBubble(threads, unread, chatOpenRef.current ? activeThreadRef.current : null);
          if (!b) return;
          const open = () => { setChatOpen(true); if (b.threadId) setActiveThread(b.threadId); };
          pushApp({ icon: 'chat', title: b.title, body: b.body, actionLabel: b.threadId ? 'Reply' : 'Open', action: open });
          playChime();
        });
    }
    prevUnread.current = unread;
    // a new version has been deployed → let people know and refresh so they get it (some never do it
    // themselves), but only once nothing is being edited: a refresh mid-form would lose what was typed.
    if (poll.data.version && poll.data.version !== APP_VERSION && !reloading.current) {
      reloading.current = true;
      const refresh = () => window.location.reload();
      const waitForCalm = () => {
        if (safeToRefresh()) refresh();
        else window.setTimeout(waitForCalm, 3000);
      };
      const now = safeToRefresh();
      pushApp({
        icon: 'update',
        title: 'New version available',
        body: now ? 'Refreshing to update…' : 'Rolodex refreshes once you finish what you are editing.',
        actionLabel: 'Refresh now',
        action: refresh,
        sticky: true,
      });
      window.setTimeout(waitForCalm, 2500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poll.dataUpdatedAt, poll.data, pushApp]);

  // new in-app notifications (the bell): pop a toast for anything that arrived since the last check.
  const notif = useNotifications();
  const lastNotifId = useRef<number | undefined>(undefined);
  useEffect(() => {
    const items = notif.data?.notifications ?? [];
    if (!items.length) return;
    const maxId = Math.max(...items.map((n) => n.id));
    if (lastNotifId.current === undefined) { lastNotifId.current = maxId; return; } // baseline on first load
    if (maxId <= lastNotifId.current) return;
    const fresh = items.filter((n) => n.id > lastNotifId.current!);
    lastNotifId.current = maxId;
    if (fresh.length) {
      // a burst never stacks past the screen: the newest ones each get a bubble, the rest one between them
      const { each, more } = notificationBubbles(fresh);
      for (const n of [...each].reverse()) {
        const link = notificationLink(n);
        pushApp({ icon: 'bell', title: n.title, body: n.message ?? undefined, ...(link ? { actionLabel: 'Open', action: () => navigate(link) } : {}) });
      }
      if (more) pushApp({ icon: 'bell', title: `${more} more notification${more === 1 ? '' : 's'}`, body: 'They are all under the bell.' });
      playChime();
    }
  }, [notif.data, pushApp]);

  const dismissToast = (id: number) => setToasts((t) => t.filter((x) => x.id !== id));

  // ── co-op session ──────────────────────────────────────────────────────────────────────────
  const pointer = useRef<{ x: number; y: number } | null>(null); // my pointer, in viewport px
  const lastInput = useRef(0); // my last click / key (a filter change right after one is mine)
  const kick = useRef<(() => void) | null>(null); // sync now (a filter or the page just changed)
  // shared filter/view state. Several components on a page can each register a slice (keyed); we merge
  // them into one canonical JSON to send, and fan an incoming state out to every slice.
  const coopView = useRef<string | null>(null);
  const viewLead = useRef(false); // the current coopView is a change of mine (see LEAD_INPUT_MS)
  const appliedView = useRef<string | null>(null); // the room's filters as last applied here
  const coopApply = useRef<((v: Record<string, unknown>) => void) | null>(null);
  const coopSlices = useRef(new Map<string, { state: string; apply: (v: Record<string, unknown>) => void }>());
  const recomputeCoopView = () => {
    const merged: Record<string, unknown> = {};
    const applies: Array<(v: Record<string, unknown>) => void> = [];
    for (const s of coopSlices.current.values()) {
      try { Object.assign(merged, JSON.parse(s.state)); } catch { /* ignore a bad slice */ }
      applies.push(s.apply);
    }
    const keys = Object.keys(merged).sort(); // canonical key order so both peers produce identical JSON
    const next = keys.length ? JSON.stringify(Object.fromEntries(keys.map((k) => [k, merged[k]]))) : null;
    coopApply.current = applies.length ? (v) => applies.forEach((fn) => fn(v)) : null;
    if (next === coopView.current) return;
    coopView.current = next;
    const now = Date.now();
    viewLead.current = next != null && next !== appliedView.current
      && (now - lastInput.current < LEAD_INPUT_MS || (!arrived.current.followed && now - arrived.current.at < LEAD_ARRIVAL_MS));
    kick.current?.();
  };
  const registerCoopView = useCallback(
    (key: string, state: string | null, apply: ((v: Record<string, unknown>) => void) | null) => {
      if (state == null || apply == null) coopSlices.current.delete(key);
      else coopSlices.current.set(key, { state, apply });
      recomputeCoopView();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  useEffect(() => {
    scrollShare.current?.newPage(); // the last page's scroll means nothing here
    kick.current?.();
  }, [pathname]);
  useEffect(() => {
    scrollTogetherRef.current = scrollTogether;
    scrollShare.current?.setOn(scrollTogether);
    kick.current?.();
  }, [scrollTogether]);

  const enterCoop = useCallback((r: CoopRoom) => { room.current = r; writeCoop(r); setCoopOn(true); }, []);
  const leaveCoop = useCallback(() => { room.current = { id: null, named: false }; writeCoop(null); setCoopOn(false); }, []);

  // The loop: send my room, page, pointer, filters and scroll, and what I'm doing; get back the room's
  // people, page, filters and scroll. No sockets, so the cadence adapts: fast while anyone is moving or
  // scrolling (peers' cursors glide and the scroll eases between polls, see CoopCursors and coopScroll),
  // calm while everyone just looks, slow alone. It pauses in a background tab, and a tab nobody touches
  // ends its co-op (sooner when alone) so a forgotten one can't keep writing to the DB.
  useEffect(() => {
    if (!coopOn) return;
    const FAST_MS = 400; // someone is moving
    const CALM_MS = 1000; // together, nobody moving
    const ALONE_MS = 2500; // waiting for others: a cheap keep-alive so a joiner still finds you
    const MOVING_MS = 2000; // a move (mine or a peer's) keeps the fast cadence this long
    const MIN_GAP_MS = 250; // syncs asked for at once (a keystroke per filter change) go out at most this often
    const IDLE_ALONE_MS = 3 * 60_000;
    const IDLE_TOGETHER_MS = 10 * 60_000;
    const REFRESH_MS = 300; // a save by someone in the room refreshes my data this soon (several saves: once)
    const REFRESH_GAP_MS = 1500; // and at most this often while they keep saving (the sheets behind are rate-limited)

    let lastActive = Date.now(); // any input here, or a peer moving while this tab is in view
    let lastMove = 0;
    let lastPeerMove = 0;
    let peersSig = '';
    let havePeers = false;
    let stopped = false;
    let inFlight = false;
    let again = false;
    let timer: number | undefined;
    let savedAt = 0;
    let sentAt = 0;
    let click: CoopClick | null = null; // a click since the last heartbeat
    let saved = 0; // when I last saved something
    const seenSaved = new Map<string, number>(); // peer → the last save of theirs I've refreshed for
    let refreshTimer: number | undefined;
    let refreshedAt = 0;
    const soon = () => Math.max(0, sentAt + MIN_GAP_MS - Date.now());
    const moving = () => {
      const now = Date.now();
      const wasStill = now - lastMove > MOVING_MS;
      lastMove = lastActive = now;
      // the first move after a still spell goes out now, not when the calm timer comes round
      if (wasStill) kick.current?.();
    };

    const onPointer = (e: PointerEvent) => {
      pointer.current = { x: e.clientX, y: e.clientY };
      if (e.type === 'pointerdown') { lastInput.current = Date.now(); kick.current?.(); }
      moving();
    };
    // a mouse that leaves the window isn't pointing at anything here: the others stop seeing it
    const onLeave = (e: PointerEvent) => {
      if (e.relatedTarget || e.pointerType === 'touch' || !pointer.current) return;
      pointer.current = null;
      moving();
    };
    const onKey = () => { lastActive = lastInput.current = Date.now(); };
    const onScroll = () => { lastActive = Date.now(); };
    const onVisible = () => { if (!document.hidden) kick.current?.(); };
    // a click on the page shows the others a ripple there (a key "clicking" a button has no spot to show)
    const onClick = (e: MouseEvent) => {
      if (e.detail === 0 || !(e.target instanceof Element) || e.target.closest('.coop-banner, .coop-layer')) return;
      const a = anchorOf(e.target, e.clientX, e.clientY);
      if (!a || !isPageAnchor(a)) return;
      click = { a, n: Date.now() };
      kick.current?.();
    };
    // the field I'm in shows on the others' screens; say so as soon as it changes
    const onFocus = () => kick.current?.();
    // closing the tab leaves at once, so the others don't see a frozen pointer until it goes stale (a
    // reload comes straight back into the room, see readCoop)
    const onHide = () => { try { navigator.sendBeacon?.('/api/collab/coop-leave'); } catch { /* best effort */ } };
    window.addEventListener('pointermove', onPointer, { passive: true });
    window.addEventListener('pointerdown', onPointer, { capture: true, passive: true });
    window.addEventListener('keydown', onKey, { capture: true });
    window.addEventListener('wheel', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { capture: true, passive: true });
    window.addEventListener('click', onClick, { capture: true, passive: true });
    document.addEventListener('pointerout', onLeave);
    document.addEventListener('focusin', onFocus);
    document.addEventListener('focusout', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pagehide', onHide);
    // my own scrolling counts as moving: the fast cadence carries it, and my pointer's spot, out promptly
    const share = createScrollShare({ on: scrollTogetherRef.current, onOwnScroll: moving });
    scrollShare.current = share;
    // anything saved here (a mutation that went through) tells the room, which refreshes its data
    const unsubscribe = queryClient.getMutationCache().subscribe((e) => {
      if (e.type !== 'updated' || e.action.type !== 'success') return;
      if (e.mutation.options.meta?.coopQuiet) return; // a chat message, a poke, something marked read: mine alone
      saved = Date.now();
      kick.current?.();
    });
    // someone else saved: refetch what's on screen (and mark the rest stale) - not the co-op's own polls.
    // One refresh picks up every save made before it, so a pending one is left to run.
    const refresh = () => {
      if (refreshTimer !== undefined) return;
      refreshTimer = window.setTimeout(() => {
        refreshTimer = undefined;
        refreshedAt = Date.now();
        void queryClient.invalidateQueries({
          predicate: (q) => q.queryKey[0] !== 'collab' && q.queryKey[0] !== 'presence',
          refetchType: 'active',
        });
      }, Math.max(REFRESH_MS, refreshedAt + REFRESH_GAP_MS - Date.now()));
    };

    const schedule = () => {
      if (stopped) return;
      const now = Date.now();
      const busy = now - lastMove < MOVING_MS || now - lastPeerMove < MOVING_MS;
      timer = window.setTimeout(run, busy ? FAST_MS : havePeers ? CALM_MS : ALONE_MS);
    };
    async function run() {
      if (stopped) return;
      if (Date.now() - lastActive > (havePeers ? IDLE_TOGETHER_MS : IDLE_ALONE_MS)) {
        leaveCoop();
        pushApp({ icon: 'bell', title: 'Co-op ended', body: 'Nothing happened in this tab for a while.' });
        return;
      }
      if (document.hidden) { schedule(); return; } // don't broadcast/poll from a background tab
      inFlight = true;
      sentAt = Date.now();
      const sentRoom = room.current;
      const sentPath = pathRef.current;
      const sentView = coopView.current;
      const sentClick = click;
      click = null; // a click goes out once; a lost one isn't worth resending
      const p = pointer.current;
      const vw = window.innerWidth || 1;
      const vh = window.innerHeight || 1;
      const hit = p && typeof document.elementFromPoint === 'function' ? document.elementFromPoint(p.x, p.y) : null;
      const anchor = p && hit ? anchorOf(hit, p.x, p.y) : null;
      // while my view moves with the room's (I'm not looking around alone, nor holding it for a field I'm
      // editing), where my pointer is in what I see of its grid, so the others can draw it there while our
      // views catch up with each other
      const main = pageScroller();
      const frame = anchor && hit && main && scrollTogetherRef.current && !share.held() ? frameOf(hit, main) : null;
      const scroll = share.mine();
      try {
        const r = await api.post<{ room: string; peers: CoopMate[]; followPath: string | null; followView: string | null; followScroll: CoopScroll | null }>('/api/collab/coop', {
          path: sentPath,
          room: sentRoom.named && sentRoom.id ? sentRoom.id : undefined,
          followed: followedPath.current === sentPath,
          x: p ? p.x / vw : -1, // -1: no pointer here (not moved yet, or out of the window)
          y: p ? p.y / vh : -1,
          anchor,
          frame,
          view: sentView ?? undefined,
          viewLead: viewLead.current,
          scroll: scroll.scroll,
          scrollLead: scroll.lead,
          scrollAge: scroll.lead ? scroll.age : undefined,
          scrollOwn: scroll.own,
          click: sentClick ?? undefined,
          focus: editingAnchor(),
          saved,
        });
        if (stopped) return;
        if (room.current === sentRoom) {
          // where the server placed me (a named room comes back as sent); a room switched meanwhile (an
          // invite sent or answered while this was in flight) wins
          room.current = { id: r.room, named: sentRoom.named };
          if (Date.now() - savedAt > 5000 || sentRoom.id !== r.room) { writeCoop(room.current); savedAt = Date.now(); }
        }
        havePeers = r.peers.length > 0;
        const sig = r.peers.map((x) => `${x.uid}:${x.path}:${x.x.toFixed(3)},${x.y.toFixed(3)}:${x.anchor ? JSON.stringify(x.anchor) : ''}:${x.frame ? JSON.stringify(x.frame) : ''}:${x.click?.n ?? ''}:${x.focus ? JSON.stringify(x.focus) : ''}`).join('|')
          + JSON.stringify(r.followScroll);
        if (sig !== peersSig) {
          peersSig = sig;
          lastPeerMove = Date.now();
          if (!document.hidden) lastActive = lastPeerMove; // being shown something counts as being here
        }
        setCoopPeers(r.peers);
        // a save by someone in the room since I last looked: refresh my data (a newcomer's last save is
        // only noted). Their saves only ever go up, so one from before they reloaded doesn't count twice.
        let fresh = false;
        for (const peer of r.peers) {
          const before = seenSaved.get(peer.uid);
          if (before !== undefined && peer.saved > before) fresh = true;
          seenSaved.set(peer.uid, Math.max(before ?? 0, peer.saved));
        }
        for (const uid of [...seenSaved.keys()]) if (!r.peers.some((peer) => peer.uid === uid)) seenSaved.delete(uid);
        if (fresh) refresh();
        // follow the room's page first; once on it, its filters and its scroll. Only act on an answer to the
        // state I'm still in: a reply sent before I moved on (or changed a filter) would drag me back.
        if (r.followPath && r.followPath !== pathRef.current) {
          if (pathRef.current === sentPath) { followedPath.current = r.followPath; navigate(r.followPath); }
        } else {
          if (r.followView && coopApply.current && r.followView !== coopView.current && coopView.current === sentView) {
            appliedView.current = r.followView;
            try { coopApply.current(JSON.parse(r.followView)); } catch { /* ignore a malformed view */ }
          }
          if (pathRef.current === sentPath) share.follow(r.followScroll);
        }
      } catch { /* transient - keep going */ }
      inFlight = false;
      if (again) { again = false; timer = window.setTimeout(run, soon()); } else schedule();
    }
    kick.current = () => {
      if (stopped) return;
      if (inFlight) { again = true; return; }
      window.clearTimeout(timer);
      timer = window.setTimeout(run, soon());
    };
    void run();

    return () => {
      stopped = true;
      kick.current = null;
      window.clearTimeout(timer);
      window.clearTimeout(refreshTimer);
      window.removeEventListener('pointermove', onPointer);
      window.removeEventListener('pointerdown', onPointer, { capture: true });
      window.removeEventListener('keydown', onKey, { capture: true });
      window.removeEventListener('wheel', onScroll);
      window.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('click', onClick, { capture: true });
      document.removeEventListener('pointerout', onLeave);
      document.removeEventListener('focusin', onFocus);
      document.removeEventListener('focusout', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pagehide', onHide);
      unsubscribe();
      share.stop();
      scrollShare.current = null;
      setCoopPeers([]);
      api.post('/api/collab/coop-leave').catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coopOn]);

  const value = useMemo<CollabValue>(() => ({
    unread: poll.data?.unread ?? 0,
    chatOpen,
    activeThread,
    openChat: (threadId) => { setChatOpen(true); if (threadId) setActiveThread(threadId); },
    closeChat: () => setChatOpen(false),
    setActiveThread,
    openDmWith: (uid) => {
      setChatOpen(true);
      // on failure land on the thread list (not a previously open thread), where dmError shows.
      createThread.mutate({ memberUids: [uid] }, { onSuccess: (r) => setActiveThread(r.id), onError: () => setActiveThread(null) });
    },
    dmError: createThread.error,
    clearDmError: () => createThread.reset(),
    nudge: (uid, kind = 'poke') => nudgeMut.mutate({ toUid: uid, kind, path: pathRef.current }),
    nudgeError: nudgeMut.error ? { kind: nudgeMut.variables?.kind ?? 'poke', error: nudgeMut.error } : null,
    clearNudgeError: () => nudgeMut.reset(),
    toasts,
    dismissToast,
    joinCoop: (path, roomId) => {
      if (path && path !== pathRef.current) { followedPath.current = path; navigate(path); }
      enterCoop({ id: roomId ?? null, named: !!roomId });
    },
    coopOn,
    coopPeers,
    toggleCoop: () => (coopOn ? leaveCoop() : enterCoop({ id: null, named: false })),
    coopScrollTogether: scrollTogether,
    setCoopScrollTogether: (on) => { writeScrollTogether(on); setScrollTogether(on); },
    coopViewHeld: () => scrollShare.current?.held() ?? false,
    coopViewPaused: () => scrollShare.current?.paused() ?? false,
    // invite into my room: the one I'm in (now named, so it stays together), or a new one
    startCoopWith: (uid) => {
      const id = coopOn && room.current.id ? room.current.id : newRoomId();
      enterCoop({ id, named: true });
      nudgeMut.mutate({ toUid: uid, kind: 'coop', path: pathRef.current, room: id });
    },
    registerCoopView,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [poll.data?.unread, chatOpen, activeThread, toasts, coopOn, coopPeers, scrollTogether, registerCoopView, enterCoop, leaveCoop, createThread.error, nudgeMut.error]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCollab(): CollabValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useCollab must be used within <CollabProvider>');
  return v;
}

/** A page (or a component in it) calls this to share a slice of its filter/view state over co-op:
 *  `state` is any JSON-serialisable object of filters; `apply` sets them from an incoming state (it
 *  receives the whole merged state and picks the keys it owns). Sharing/applying only takes effect
 *  while a co-op session is active — otherwise it's a no-op that just tracks the current state. */
export function useCoopView(state: unknown, apply: (v: Record<string, unknown>) => void): void {
  const { registerCoopView } = useCollab();
  const key = useId();
  const json = JSON.stringify(state ?? null);
  const applyRef = useRef(apply);
  applyRef.current = apply;
  useEffect(() => {
    registerCoopView(key, json, (v) => applyRef.current(v));
  }, [key, json, registerCoopView]);
  useEffect(() => () => registerCoopView(key, null, null), [key, registerCoopView]);
}
